import { type CreateOptions } from '@anycable/core'
import { type CableSubscriptionEventHandler } from './CableSubscriptionEventHandler'
import { type MessageStructure } from './types'

import { countBy } from 'lodash'
import { registerAtomicTransaction } from 'utils/error'
import { DebugWebSocket } from '../DebugWebSocket'
import { isDocumentHidden } from 'utils'

/*
  This is a first attempt at fixing some of the disconnect issues, mainly on the RealtimeProvider but wide spread on the application
*/
export class CableConnectionManager {
  _toSend: MessageStructure[] = []
  _websocket: WebSocket | DebugWebSocket | null = null
  _actionInterval: NodeJS.Timeout | null = null
  _isOffline: boolean = false
  _notifiedAllHandlersOfNetworkOutage: boolean = false
  _isReady: boolean = false
  _isIntentionalDisconnect: boolean = false
  _handlers: Record<string, CableSubscriptionEventHandler<any, any>[]> = {}
  _lastThreshold: number = 0
  _lastReceived: number | null = null
  _lastReconnect: number | null = null
  constructor(
    readonly url: string,
    readonly fallbackUrl: string,
    _opts: Partial<CreateOptions>,
    readonly hearbeatInterval: number = 50,
  ) {
    this.setupWebSocket()
    // Also set in onOnline
    this._actionInterval = setInterval(this.sendStep, this.hearbeatInterval)
    // Disable the actionInterval if offline
    window.addEventListener('offline', this.onOffline)
    // Re-enable the actionInterval when back online
    window.addEventListener('online', this.onOnline)
    // TODO: Handle doc visibilitychanges
    // document.addEventListener("visibilitychange", this.onVisibilityChange)
  }
  // CALLED BY WEBSOCKET
  _onClose = (ev: CloseEvent) => {
    this._websocket = null
    this._isReady = false
    if (!this._isIntentionalDisconnect) {
      this.resubscribeAll()
    }
    // NOTICE: I chose to leave all handlers in the dark about the disconnect and just resubscribe
    // this._handlers.forEach(handler => handler.disconnected())
  }
  _onError = (ev: Event) => {
    // WHY: It is hard to say which channel errored
    // All the scenarios I have seen close the websocket
    // I'm choosing to keep quiet about this and let onclose handle it

    // PROD: The websocket closes with an error everytime we deploy rails

    // DEV: DebugWebSocket already reports this to Sentry

    console.debug('[conn] websocket.onerror - ignored') // eslint-disable-line no-console
  }
  _onMessage = (ev: MessageEvent) => {
    this._lastReceived = Date.now()
    const message = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data
    const channelId = message.identifier
    // Nothing to do with this for now
    if (message.type === 'welcome') {
      return
    }
    // Any message is as good as a ping
    if (message.type === 'ping') {
      return
    }
    // We do not disconnect from our server, something went wrong
    // Example: {"type":"disconnect","reason":"server_restart","reconnect":true}
    if (message.type === 'disconnect') {
      // Reporting disconnecting channels
      registerAtomicTransaction(`DebugConsumerDisconnect`, {
        reason: message.reason,
        reconnect: message.reconnect,
      })
      // We'll just try to reconnect
      const reason =
        message.reason === 'server_restart' ? 'DebugConsumerServerRestart' : 'DebugConsumerServerUnknownDisconnect'
      this.silentlyCloseCurrentWebSocket(1000, reason)
      this.setupWebSocket()
      return
    }
    if (this._handlers[channelId]) {
      this._handlers[channelId].forEach((handler) => handler.handleMessage(message))
    } else {
      registerAtomicTransaction(`DebugConsumerUnknownChannel`, {
        channelId,
        data: ev.data,
      })
    }
  }
  _onOpen = (ev: Event) => {
    this._isReady = true
    // Reset this in case we lose network again and the websocket closes
    this._notifiedAllHandlersOfNetworkOutage = false
    this.resubscribeAll()
    this._toSend.forEach((payload) => this._websocket && this._isReady && this._websocket.send(JSON.stringify(payload)))
    this._toSend = []
    this._lastReceived = Date.now()
  }
  // CALLED BY SUBSCRIPTION
  send(payload: MessageStructure) {
    this._toSend.push(payload)
  }
  registerHandler(channelIdentifier: string, handler: CableSubscriptionEventHandler<any, any>) {
    // Add handler to channel index
    if (!this._handlers[channelIdentifier]) this._handlers[channelIdentifier] = []
    this._handlers[channelIdentifier].push(handler)

    // REVIEW: Call initialized
    if (this._websocket && this._isReady) {
      setTimeout(() => handler.initialized())
    }

    // Bypass double subscription
    const targetState = this.pendingTargetState()
    const alreadySubscribing = targetState[channelIdentifier]
    if (!alreadySubscribing) {
      this._toSend.push({ command: 'subscribe', identifier: channelIdentifier })
    }

    // Remove oneself
    return () => {
      // It forgets all handlers when intentionally disconnecting
      if (this._handlers[channelIdentifier]) {
        const idx = this._handlers[channelIdentifier].indexOf(handler)
        this._handlers[channelIdentifier].splice(idx, 1)
        this.cleanupEmptyChannels(channelIdentifier)
      }
    }
  }
  // CALLED BY CABLE
  disconnect() {
    // This instance is not reusable
    if (this._actionInterval) clearInterval(this._actionInterval)
    // Let onClose know
    this._isIntentionalDisconnect = true
    // Send the last messages we got queued
    if (this._websocket && this._isReady) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this._toSend.forEach((payload) => this._websocket!.send(JSON.stringify(payload)))
      this._toSend = []
      this.silentlyCloseCurrentWebSocket(1000, 'DebugConsumerNormalDisconnect')
    } else {
      this.silentlyCloseCurrentWebSocket(1000, 'DebugConsumerEarlyDisconnect')
    }
    // Let everyone know we are closed
    for (const channelId in this._handlers) {
      this._handlers[channelId].forEach((handler) => handler.disconnected())
    }
    // Drop every handler
    this._handlers = {}
  }
  // INTERNAL
  // Attempt to send a single message every interval
  private sendStep = () => {
    // Too long since last message, force disconnect, irrelevant if we are disconnected
    if (this._websocket && this._isReady && this._lastReceived && Date.now() - this._lastReceived > 10 ** 4) {
      // Not really news if the tab is hidden
      if (!isDocumentHidden()) {
        registerAtomicTransaction(`DebugConsumerLastReceivedTooLongAgo`, {
          lastReceived: this._lastReceived,
        })
        // Don't report again until the next 10 seconds
        this._lastReceived = Date.now()
      }
      this.silentlyCloseCurrentWebSocket(1000, 'DebugConsumerLastReceivedTooLongAgo')
      this.setupWebSocket()
    }
    if (this._websocket && this._isReady) {
      // Send message
      const item = this._toSend.shift()
      if (item) {
        this._websocket.send(JSON.stringify(item))
        this.reportQueueLength()
      }
    } else if (this._websocket && this.isWebsocketUnableToConnect()) {
      // The websocket is stuck in connecting, try the fallback url
      registerAtomicTransaction('DebugConsumerLikelyBlockedByPlugin', {})
      console.debug('[conn] websocket not ready for too long, trying fallback url') // eslint-disable-line no-console
      this.silentlyCloseCurrentWebSocket(4000, 'WebSocketStuckInConnecting')
      this.setupWebSocket()
    } else if (this._websocket) {
      // Websocket is just not ready, we dump the websocket when it closes
      console.debug('[conn] websocket is not ready') // eslint-disable-line no-console
    } else {
      // CAUTION: No delay can cause a client stampede, but keeping this minimal makes it easier
      //          And this is already under an unsync heartbeat
      this.setupWebSocket()
    }
  }
  // DEV: Just for debugging
  private reportQueueLength() {
    // Only report on increments of 10
    const currentQueueSize = Math.floor(this._toSend.length / 10)
    if (currentQueueSize === this._lastThreshold) {
      return
    }
    this._lastThreshold = currentQueueSize
    if (currentQueueSize > 0) {
      // eslint-disable-next-line no-console
      console.debug(
        `[conn] [debug-consumer] queue size: ${currentQueueSize * 10}`,
        Object.keys(this._handlers).length,
        countBy(this._toSend, (message) => message.command),
        countBy(this._toSend, (message) => message.data),
      )
    }
  }
  private isWebsocketUnableToConnect(): boolean {
    const neverReceived = this._lastReceived == null
    const lastConnectedLongAgo = this._lastReconnect != null && Date.now() - this._lastReconnect > 3000
    return !this._isReady && neverReceived && lastConnectedLongAgo
  }
  private onOffline = () => {
    this._isOffline = true
    if (this._actionInterval) {
      clearInterval(this._actionInterval)
      this._actionInterval = null
    }
    // We don't want to let anyone know we lost our connection until the websocket closes
    //   If we notify on window.offline and the websocket is still open we get stuck in a limbo
    // If the websocket dies, we will reconnect and re-subscribe
    //   Unless there's no network, in which case we should notify the handlers so RealtimeProvider can do its thing
  }
  private onOnline = () => {
    this._isOffline = false
    if (!this._actionInterval) {
      this._actionInterval = setInterval(this.sendStep, this.hearbeatInterval)
    }
  }
  private resubscribeAll() {
    const targetState = this.pendingTargetState()
    // Subscribe to anything not seen in the messages
    for (const channelId in this._handlers) {
      if (targetState[channelId] == null) {
        this._toSend.unshift({ command: 'subscribe', identifier: channelId })
      }
    }
  }
  private cleanupEmptyChannels(channelIdentifier: string) {
    // If we still have handlers, don't drop anything
    if (this._handlers[channelIdentifier].length) return
    // Drop the empty handler list
    delete this._handlers[channelIdentifier]
    // Check for pending subscriptions
    const pendingSubscription: Record<string, boolean> = {}
    // Drop the channel messages
    this._toSend = this._toSend.filter(({ command, identifier }) => {
      // Check for this channel's subscription
      if (command === 'subscribe' && identifier === channelIdentifier) {
        pendingSubscription[identifier] = true
      }
      // If we didn't subscribe yet, drop all messages
      if (pendingSubscription[identifier]) {
        return false
      }
      // If we potentially subscribed, keep the unsubscribe
      return command === 'unsubscribe' || identifier !== channelIdentifier
    })
  }
  private setupWebSocket() {
    // We just tried less than a second ago, wait a bit
    if (this._lastReconnect && Date.now() - this._lastReconnect < 10 ** 3) {
      return
    }
    // Let's not try to setup the socket yet, wait for the window to be visible
    if (isDocumentHidden()) {
      console.debug('[conn] websocket disconnected but the document is not visible, ignoring') // eslint-disable-line no-console
      return
    }
    if (this._isOffline) {
      console.debug('[conn] websocket disconnected but the window is offline, ignoring') // eslint-disable-line no-console
      // If we are offline, we better tell all the handlers
      if (!this._notifiedAllHandlersOfNetworkOutage) {
        this._notifiedAllHandlersOfNetworkOutage = true
        for (const channel in this._handlers) {
          this._handlers[channel].forEach((handler) => handler.disconnected())
        }
      }
      return
    }
    try {
      if (!this._websocket) {
        const url = this.isWebsocketUnableToConnect() ? this.fallbackUrl : this.url
        this._websocket = new DebugWebSocket(url)
      }
    } catch {
      // We already report these errors in DebugWebSocket
    } finally {
      this._lastReconnect = Date.now()
    }
    if (this._websocket) {
      this._websocket.onclose = this._onClose
      this._websocket.onerror = this._onError
      this._websocket.onmessage = this._onMessage
      this._websocket.onopen = this._onOpen
    }
  }
  private silentlyCloseCurrentWebSocket(code: number, reason: string) {
    if (this._websocket) {
      // Silence all calls
      this._websocket.onclose = null
      this._websocket.onerror = null
      this._websocket.onmessage = null
      this._websocket.onopen = null
      // Close it
      this._websocket.close(code, reason)
      this._websocket = null
      this._isReady = false
    }
  }
  /* For all messages to send, per channel Id:
  - true *subscribe*
  - false *unsubscribe*
  - undefined *nothing in _toSend*
  */
  private pendingTargetState() {
    // Backwards, record the ongoing subscriptions and unsubscriptions
    const subscribing: Record<string, boolean> = {}
    for (let i = this._toSend.length; i--; ) {
      const message = this._toSend[i]
      if (message && message.identifier in subscribing) {
        continue
      } else if (message && message.command === 'subscribe') {
        subscribing[message.identifier] = true
      } else if (message && message.command === 'unsubscribe') {
        subscribing[message.identifier] = false
      }
    }
    return subscribing
  }
}
